import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { BtnTeal } from './Primitives.styles';
import { SHELL_BREAK } from './Shell.styles';

const cardIn = keyframes`
	from {
		opacity: 0;
		transform: translateY(22px);
	}
`;

export const AuthSplit = styled.section`
	/* the split lives in a rounded container inset from the page edges
	   (homepage media-block geometry), sitting below the island bar */
	--stack-top: 96px;
	--stack-bottom: clamp(24px, 4vh, 48px);
	margin: var(--stack-top) var(--gut) var(--stack-bottom);
	border-radius: var(--r);
	border: 1px solid var(--hair);
	overflow: hidden;
	background: var(--cream);
	box-shadow: 0 18px 44px rgba(0, 0, 0, 0.1);
	display: grid;
	grid-template-columns: 1.05fr 1fr;
	min-height: calc(100vh - var(--stack-top) - var(--stack-bottom));
	animation: ${cardIn} 0.7s cubic-bezier(0.2, 0.65, 0.25, 1) backwards;

	/* staggered entrance inside the card; the hand and watermark keep their
	   base transforms, so they fade opacity-only */
	.stage h1,
	.vlist li,
	.formcol .inner {
		animation: ${cardIn} 0.6s cubic-bezier(0.2, 0.65, 0.25, 1) backwards;
	}

	.stage h1 {
		animation-delay: 0.15s;
	}

	.vlist li:nth-child(1) {
		animation-delay: 0.25s;
	}

	.vlist li:nth-child(2) {
		animation-delay: 0.32s;
	}

	.vlist li:nth-child(3) {
		animation-delay: 0.39s;
	}

	.formcol .inner {
		animation-delay: 0.2s;
	}

	.stage .hand,
	.stage .wmk {
		animation: auth-fade 0.7s ease 0.25s backwards;
	}

	.stage .wmk {
		animation-delay: 0.45s;
	}

	@keyframes auth-fade {
		from {
			opacity: 0;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		&,
		.stage h1,
		.vlist li,
		.formcol .inner,
		.stage .hand,
		.stage .wmk {
			animation: none;
		}
	}

	.stage {
		position: relative;
		overflow: hidden;
		min-width: 0;
		background: var(--panel);
		/* left edge lines up with the wordmark inside the island bar
		   (bar at var(--pad) + 22px inner padding, minus the container inset) */
		padding: clamp(44px, 6vh, 80px) clamp(34px, 4.5vw, 64px)
			clamp(44px, 6vh, 80px) calc(var(--pad) + 22px - var(--gut));
		display: flex;
		flex-direction: column;
		justify-content: center;
		gap: 20px;
	}

	.stage .wmk {
		position: absolute;
		left: -8px;
		bottom: -16px;
		font-family: var(--fd);
		font-size: clamp(80px, 10vw, 140px);
		line-height: 0.92;
		text-transform: uppercase;
		color: transparent;
		-webkit-text-stroke: 1.5px rgba(var(--ink-rgb), 0.12);
		white-space: nowrap;
		pointer-events: none;
		user-select: none;
	}

	.stage .hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--gold-ink);
		font-size: clamp(22px, 2.2vw, 30px);
		transform: rotate(-1.8deg);
		width: max-content;
	}

	.stage h1 {
		margin: 0;
		font-size: clamp(34px, 3.6vw, 56px);
	}

	.stage h1 em {
		font-style: normal;
		color: var(--coral);
	}

	.vlist {
		list-style: none;
		display: flex;
		flex-direction: column;
		gap: 12px;
		margin: 8px 0 0;
		padding: 0;
		position: relative;
	}

	.vlist li {
		display: flex;
		align-items: center;
		gap: 12px;
		font-size: 16px;
		font-weight: 500;
		color: var(--brown);
	}

	.vlist .tick {
		width: 26px;
		height: 26px;
		flex-shrink: 0;
		border-radius: 50%;
		background: var(--teal);
		color: var(--teal-deep);
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.formcol {
		min-width: 0;
		padding: clamp(44px, 6vh, 80px) clamp(34px, 4.5vw, 64px);
		display: flex;
		flex-direction: column;
		justify-content: center;
	}

	.inner {
		max-width: 440px;
		width: 100%;
	}

	.atabs {
		display: flex;
		gap: 22px;
		margin-bottom: 26px;
	}

	.atabs button {
		font-family: var(--fd);
		font-size: 17px;
		letter-spacing: 0.4px;
		text-transform: uppercase;
		background: none;
		border: none;
		color: var(--mut);
		padding: 0 0 5px;
		border-bottom: 2px solid transparent;
		cursor: pointer;
		transition: 0.2s;
	}

	.atabs button:hover {
		color: var(--ink);
	}

	.atabs button.on {
		color: var(--teal-dark);
		border-color: var(--teal-dark);
	}

	/* Google sign-in: the GIS iframe can't be styled and caps at 400px,
	   so a design-system pill (input metrics) is the visual and the real
	   invisible GIS button is scaled over it — clicks land on Google's
	   iframe, the redirect flow stays untouched */
	.gwrap {
		position: relative;
		max-width: 100%;
	}

	.gwrap .gbtn {
		pointer-events: none;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 12px;
		width: 100%;
		padding: 13px 22px;
		border-radius: var(--r-pill);
		border: 1.5px solid rgba(27, 20, 14, 0.16);
		background: ${({ theme }) =>
			theme.mode === 'dark' ? '#F2EFEA' : '#FFFFFF'};
		color: #1b140e;
		font-family: var(--fb);
		font-size: 15.5px;
		font-weight: 600;
		/* inputs use the UA's normal line-height (~20px content box) —
		   mirror it so both pills land on exactly the same height */
		line-height: 20px;
		transition:
			transform 0.18s,
			box-shadow 0.2s;
	}

	.gwrap:hover .gbtn {
		transform: translateY(-1px);
		box-shadow: 0 10px 26px rgba(0, 0, 0, 0.12);
	}

	.gwrap .gis {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0.001;
		transform-origin: 0 0;
	}

	.or {
		display: flex;
		align-items: center;
		gap: 14px;
		margin: 22px 0;
	}

	.or::before,
	.or::after {
		content: '';
		flex: 1;
		height: 1px;
		background: var(--hair);
	}

	.or span {
		font-family: var(--fd);
		font-size: 14px;
		letter-spacing: 1px;
		text-transform: uppercase;
		color: var(--mut);
	}

	/* forms fill the column; submit buttons stretch like the mock */
	.inner form {
		width: 100%;
	}

	/* index.css has form { all: unset } which resets box-sizing to
	   content-box for the whole form subtree — restore border-box here
	   so width:100% inputs/buttons respect their padding */
	.inner form,
	.inner form * {
		box-sizing: border-box;
	}

	.inner ${BtnTeal} {
		width: 100%;
	}

	.gnote {
		font-size: 13px;
		color: var(--mut);
		margin: 10px 0 0;
	}

	/* below the shell break the bar is a plain strip with the logo at
	   22px — the banner stage and the form column line up with it */
	${SHELL_BREAK} {
		--stack-top: 78px;
		--stack-bottom: 18px;
		grid-template-columns: 1fr;
		min-height: 0;

		.stage {
			min-height: 0;
			justify-content: flex-end;
			padding: 26px calc(22px - var(--gut)) 24px;
			gap: 10px;
		}

		.stage .wmk {
			font-size: 72px;
		}

		.stage .vlist {
			display: none;
		}

		.formcol {
			justify-content: flex-start;
			padding: 26px calc(22px - var(--gut)) 56px;
		}
	}
`;

const popIn = keyframes`
	0% { transform: scale(0); }
	70% { transform: scale(1.3); }
	100% { transform: scale(1); }
`;

const skelPulse = keyframes`
	0%, 100% { opacity: 0.55; }
	50% { opacity: 1; }
`;

/** Auth periphery (forgotten password, mail sent, new password, account
 *  confirmation, activation): same split as the auth page, but the stage
 *  title is contextual (and not the page's heading — the right column
 *  carries the real h1). Success swaps in place with a tick pop; no flash. */
export const AuthStatus = styled(AuthSplit)`
	.stage .big {
		font-family: var(--fd);
		font-size: clamp(30px, 3.2vw, 50px);
		line-height: 1.02;
		letter-spacing: 0.2px;
		color: var(--ink);
		text-transform: uppercase;
	}

	.stage .big em {
		font-style: normal;
		color: var(--coral);
	}

	.formcol p {
		color: var(--brown);
		font-size: 16px;
		margin: 0 0 18px;
	}

	.formcol p.mut {
		color: var(--mut);
		font-size: 14.5px;
	}

	.formcol p a {
		color: var(--teal-dark);
		font-weight: 600;
	}

	.okrow {
		display: flex;
		align-items: center;
		gap: 14px;
		margin-bottom: 14px;
	}

	.okrow p {
		margin: 0;
	}

	.oktick {
		width: 44px;
		height: 44px;
		flex-shrink: 0;
		border-radius: 50%;
		background: var(--teal);
		color: var(--teal-deep);
		display: flex;
		align-items: center;
		justify-content: center;
		animation: ${popIn} 0.5s cubic-bezier(0.2, 0.9, 0.3, 1.4) both;
	}

	/* success actions keep their natural width (forms stay full-width) */
	.done ${BtnTeal} {
		width: auto;
	}

	.skel {
		height: 16px;
		border-radius: 8px;
		background: rgba(var(--ink-rgb), 0.08);
		margin-bottom: 12px;
		animation: ${skelPulse} 1.2s ease-in-out infinite;
	}

	.skel.t {
		height: 34px;
		width: 60%;
		border-radius: 10px;
		margin-bottom: 18px;
	}

	@media (prefers-reduced-motion: reduce) {
		.oktick,
		.skel {
			animation: none;
		}
	}
`;

/** Page shell shared by every auth view (login/register, activation,
 *  registration success, both reset-password steps). */
export const AuthPage = styled.div`
	text-align: left;
`;

/** Login form element (plain column; the registration variant adds the
 *  two-up name row). */
export const LoginFormEl = styled.form`
	width: 100%;
	max-width: 460px;
`;

export const RegFormEl = styled.form`
	width: 100%;
	max-width: 460px;

	/* ime + prezime share one row, like the mock */
	.frow {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 0 12px;
	}

	@media (max-width: 520px) {
		.frow {
			grid-template-columns: 1fr;
			gap: 0;
		}
	}
`;

/** Pill input field shared by the login and registration forms; $hasEye
 *  reserves room for the password-visibility toggle. */
export const AuthField = styled.div<{ $hasEye?: boolean }>`
	position: relative;
	margin-bottom: 14px;

	input {
		width: 100%;
		background: var(--panel-2);
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		border-radius: var(--r-pill);
		padding: 13px 22px;
		${({ $hasEye }) => ($hasEye ? 'padding-right: 52px;' : '')}
		font-family: var(--fb);
		font-size: 16.5px;
		color: var(--ink);
		outline: none;
		transition: border-color 0.2s;
	}

	input:focus {
		border-color: var(--teal-dark);
	}

	input::placeholder {
		color: var(--mut);
	}

	.eye-icon {
		position: absolute;
		top: 50%;
		right: 16px;
		transform: translateY(-50%);
		display: inline-flex;
		align-items: center;
		font-size: 17px;
		color: var(--mut);
		cursor: pointer;
		transition: color 0.15s;
	}

	.eye-icon:hover {
		color: var(--ink);
	}
`;

export const AuthForgotLink = styled(Link)`
	display: inline-block;
	font-size: 14.5px;
	font-weight: 600;
	color: var(--teal-dark);
	text-decoration: none;
	margin: 2px 0 20px;

	&:hover {
		text-decoration: underline;
	}
`;

/** Reset password step 1: email field (no eye toggle). */
export const AuthResetEmailField = styled.div`
	position: relative;
	margin-bottom: 14px;

	input {
		width: 100%;
		background: var(--panel-2);
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		border-radius: var(--r-pill);
		padding: 13px 22px;
		font-family: var(--fb);
		font-size: 16.5px;
		color: var(--ink);
		outline: none;
		transition: border-color 0.2s;
	}

	input:focus {
		border-color: var(--teal-dark);
	}

	input::placeholder {
		color: var(--mut);
	}
`;

/** Reset password step 2: new-password field (eye toggle always on). */
export const AuthResetPassField = styled.div`
	position: relative;
	margin-bottom: 14px;

	input {
		width: 100%;
		background: var(--panel-2);
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		border-radius: var(--r-pill);
		padding: 13px 22px;
		padding-right: 52px;
		font-family: var(--fb);
		font-size: 16.5px;
		color: var(--ink);
		outline: none;
		transition: border-color 0.2s;
	}

	input:focus {
		border-color: var(--teal-dark);
	}

	input::placeholder {
		color: var(--mut);
	}

	.eye-icon {
		position: absolute;
		top: 50%;
		right: 16px;
		transform: translateY(-50%);
		display: inline-flex;
		align-items: center;
		font-size: 17px;
		color: var(--mut);
		cursor: pointer;
		transition: color 0.15s;
	}

	.eye-icon:hover {
		color: var(--ink);
	}
`;
